import { useState } from 'react';
import { Link } from 'react-router-dom';
import { readConsent } from './consent.js';
import { analyticsEnabled, setConsent } from './gtag.js';

/**
 * Asks once whether Google Analytics may store a cookie.
 *
 * Shown only where nobody has answered yet, and only in a build that ships
 * analytics at all. Declining is as cheap as accepting and both dismiss it
 * permanently: a banner whose only real button is "accept" is not consent.
 *
 * **Not rendered on an audience surface while a poll is live.** That is not
 * decided here — App mounts this for every other route, and Join mounts it on
 * the ended screen alone, so the two can never both be on the page. The
 * projector never mounts it at all: a big screen has no one to ask, and a
 * modal over a room's join code would be a fault, not a disclosure.
 *
 * Declining is not the end of counting. With storage denied GA4 still sends a
 * cookieless ping, so a page view is still counted; what a refusal buys is
 * that nothing is written to the device and this visitor is not joined up
 * across pages. The copy says so rather than implying a switch-off it does not
 * perform.
 */
/**
 * Whether this path is an audience surface, which defers the banner.
 *
 * Lives here rather than in App because it is half of the banner's placement
 * rule and meaningless apart from it — and because App renders inside a
 * BrowserRouter, which makes the rule awkward to test where it was used.
 *
 * `/j` with no code is the type-in-a-code screen and counts as audience: a
 * phone is a step away from a poll there, which is the moment the rule exists
 * to protect.
 */
export function isAudienceSurface(pathname: string): boolean {
  return pathname === '/j' || pathname.startsWith('/j/') || pathname.startsWith('/screen/');
}

/**
 * Whether the banner may be raised here, now — the whole placement rule, in
 * one testable place because both halves of it were found the hard way.
 *
 * Counting never waits; only the asking does. Nothing is stored while nobody
 * has said yes, so deferring the question costs nothing and buys a moment
 * when it is the only thing on screen.
 *
 * Two deferrals, both collisions with something that owns the screen already:
 *
 * - **An audience surface**, until the poll ends. A phone between a QR scan
 *   and its first question is mid-task, and a projector has nobody to ask.
 * - **The walkthrough**, until it is off. `.tour-dock` is fixed at the same
 *   `bottom` offset the banner centres on and one z-index above it, so a
 *   brand-new account — which is precisely an account that has neither seen
 *   the guide nor answered the banner — would get the two stacked. 'standing
 *   by' counts as running: the dock is what is on screen then, and the dock
 *   is the half that collides.
 */
export function shouldAskHere(pathname: string, tourPhase: 'off' | 'running' | 'standing-by') {
  return !isAudienceSurface(pathname) && tourPhase === 'off';
}

export function ConsentBanner() {
  const [answered, setAnswered] = useState(() => readConsent() !== undefined);

  if (answered || !analyticsEnabled()) return null;

  const answer = (choice: 'granted' | 'denied') => {
    setConsent(choice);
    setAnswered(true);
  };

  return (
    <div className="consent-banner" role="dialog" aria-label="Analytics cookies">
      <p className="consent-banner-text">
        We&rsquo;d like to use Google Analytics to count visits and see which pages are worth
        keeping. It stores a cookie on your device. Decline and we still count the visit, but
        nothing is stored and you are not followed between pages.{' '}
        <Link to="/privacy">How we handle your data</Link>.
      </p>
      <div className="consent-banner-actions">
        <button type="button" className="secondary" onClick={() => answer('denied')}>
          Decline
        </button>
        <button type="button" onClick={() => answer('granted')}>
          Accept
        </button>
      </div>
    </div>
  );
}
